import { useState, useEffect } from 'react';

import { localStorageGetItem } from 'src/utils/local-storage';

export interface PresentationPreview {
  /**
   * The PDF for react-pdf, or null until the first one arrives. The bytes themselves, not a `blob:`
   * URL: the app's Content Security Policy (`connect-src *`) doesn't allow pdf.js to read `blob:`
   * URLs, so a URL here shows "Failed to load PDF file" on staging and production but works locally.
   * A new object for each PDF, so react-pdf loads each one once.
   */
  file: { data: Uint8Array } | null;
  loading: boolean;
  /** True when the last request failed; the previous PDF stays on screen. */
  failed: boolean;
  /** The server's reason when it gave one, such as the document converter being off. */
  errorCode?: string;
  /** The HTTP status of the last failed request, for the message the builder shows. */
  status?: number;
}

/**
 * The live preview (JT1-1886): the exported PDF itself, fetched for each saved version. RTK Query
 * doesn't handle binary bodies well, so this uses fetch, as the profile export does. A newer
 * version cancels the request still in flight.
 */
export function usePresentationPreview(id: string | undefined, version: number | undefined): PresentationPreview {
  const [state, setState] = useState<PresentationPreview>({ file: null, loading: false, failed: false });

  useEffect(() => {
    if (!id || !version) {
      return undefined;
    }
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, failed: false }));

    fetch(`${import.meta.env.VITE_BACKEND_URL}/my/presentations/${id}/preview`, {
      method: 'POST',
      signal: controller.signal,
      headers: { Authorization: `Bearer ${localStorageGetItem('access_token', undefined)}`, Accept: 'application/pdf' },
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw Object.assign(new Error(`preview ${response.status}`), { code: body?.error_code as string | undefined, status: response.status });
        }
        const data = new Uint8Array(await response.arrayBuffer());
        setState({ file: { data }, loading: false, failed: false, errorCode: undefined, status: undefined });
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState((previous) => ({ ...previous, loading: false, failed: true, errorCode: error?.code, status: error?.status }));
      });

    return () => controller.abort();
  }, [id, version]);

  return state;
}

/** Downloads a file from a signed URL without leaving the page. */
export function downloadFromUrl(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
